// JWT Token utilities for magic link approvals
// In production, use a proper JWT library like 'jsonwebtoken'

interface ApprovalTokenPayload {
  requestId: string;
  managerEmail: string;
  userId: string;
  action: string;
  roles: string[];
  exp: number; // expiration timestamp
  iat: number; // issued at timestamp
}

class ApprovalTokenManager {
  private static readonly TOKEN_EXPIRY_HOURS = 48;

  // Generate a magic link token for approval
  static generateApprovalToken(requestId: string, managerEmail: string, userId: string, action: string, roles: string[]): string {
    const now = Math.floor(Date.now() / 1000);
    const payload: ApprovalTokenPayload = {
      requestId,
      managerEmail,
      userId,
      action,
      roles,
      exp: now + (this.TOKEN_EXPIRY_HOURS * 60 * 60), // 48 hours
      iat: now
    };

    // In production, use proper JWT encoding
    // For demo purposes, we'll create a simple base64 encoded token
    const tokenData = btoa(JSON.stringify(payload));
    return `approval_${tokenData}`;
  }

  // Validate and decode a magic link token
  static validateApprovalToken(token: string): ApprovalTokenPayload | null {
    try {
      if (!token.startsWith('approval_')) {
        return null;
      }

      const tokenData = token.replace('approval_', '');
      const payload: ApprovalTokenPayload = JSON.parse(atob(tokenData));

      // Check if token is expired
      const now = Math.floor(Date.now() / 1000);
      if (payload.exp < now) {
        return null;
      }

      return payload;
    } catch (error) {
      console.error('Token validation error:', error);
      return null;
    }
  }

  // Generate approval URLs
  static generateApprovalUrls(requestId: string, managerEmail: string, userId: string, action: string, roles: string[]): {
    approveUrl: string;
    rejectUrl: string;
    baseUrl: string;
  } {
    const token = this.generateApprovalToken(requestId, managerEmail, userId, action, roles);
    // Use environment variable or current origin (for production) or fallback to production URL
    const baseUrl = process.env.REACT_APP_BASE_URL || 
                    (typeof window !== 'undefined' ? window.location.origin : '') ||
                    'https://nyx-lakeland.xendl.com';
    
    return {
      approveUrl: `${baseUrl}/approve/${token}?action=approve`,
      rejectUrl: `${baseUrl}/approve/${token}?action=reject`,
      baseUrl: `${baseUrl}/approve/${token}`
    };
  }
}

export default ApprovalTokenManager;
